import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { HttpEventType } from '@angular/common/http';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { TranslateModule } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { PipesModule } from '../../../shared/pipes/pipes.module';
import { MediaService } from '../../../shared/services/media.service';

const DONE_ICON = 'done';
const BLOCK_ICON = 'done';

/** When the browser omits/mislabels MIME, accept by filename extension. */
const MIME_EXTENSION_FALLBACKS: Record<string, string[]> = {
  'application/pdf': ['.pdf'],
  'application/vnd.google-earth.kml+xml': ['.kml'],
  'application/vnd.google-earth.kmz': ['.kmz'],
};

@Component({
  selector: 'app-upload-file-with-progress',
  standalone: true,
  imports: [
    CommonModule,
    MatProgressBarModule,
    PipesModule,
    TranslateModule,
    MatIconModule,
  ],
  templateUrl: './upload-file-with-progress.component.html',
  styleUrls: ['./upload-file-with-progress.component.scss'],
})
export class UploadFileWithProgressComponent implements OnInit {
  @Input() accept = 'image/*';
  @Input() isDropFile = false;
  @Input() aligned = false;
  @Input() factoryOfUploadFunction: (file: File) => Observable<any>;
  @Input() isShowButton = true;
  @Input() incomeFile = null;
  @Output() completed = new EventEmitter<any>();
  @Output() whenUpload: EventEmitter<any> = new EventEmitter();
  @Output() whenRemoveFile: EventEmitter<any> = new EventEmitter();
  @ViewChild('fileInput') fileInput: ElementRef;
  progress = 0;
  icon = null;
  file: File = null;
  correctSize = true;
  maxVideoSize = 512;
  isSuccess = false;
  date = new Date();
  isDragging = false;
  constructor(
    private toastrService: ToastrService,
    private mediaService: MediaService,
  ) {}

  ngOnInit() {
    if (this.incomeFile) {
      this.file = this.incomeFile;
      this.progress = 100;
    }
  }

  fileInputOnChange(event, isFile = false) {
    this.icon = null;
    const selected: File = isFile ? event : event.target.files[0];
    if (!selected) {
      return;
    }

    if (!this.isFileAccepted(selected)) {
      this.toastrService.error(
        `Invalid file type. Accepted: ${this.accept}`,
      );
      this.file = null;
      this.progress = 0;
      if (!isFile && event?.target) {
        event.target.value = '';
      }
      return;
    }

    this.file = selected;
    this.progress = 0;
    if (this.file && !this.file.type.includes('video')) {
      this.correctSize = this.file?.size / 1024 / 1024 <= 2;
      if (this.isDropFile) {
        this.upload();
      }
    } else {
      this.handleFileSize();
    }
  }

  private isFileAccepted(file: File): boolean {
    if (!this.accept) {
      return true;
    }
    const accepted = this.accept
      .split(',')
      .map((token) => token.trim().toLowerCase())
      .filter(Boolean);
    if (!accepted.length) {
      return true;
    }

    const type = (file.type || '').toLowerCase();
    const name = (file.name || '').toLowerCase();

    return accepted.some((pattern) => {
      if (pattern.startsWith('.')) {
        return name.endsWith(pattern);
      }
      if (pattern.endsWith('/*')) {
        return type.startsWith(pattern.slice(0, -1));
      }
      if (type && type === pattern) {
        return true;
      }
      // Chromium often reports KML as empty / application/xml / text/xml.
      const fallbackExts = MIME_EXTENSION_FALLBACKS[pattern];
      if (fallbackExts?.some((ext) => name.endsWith(ext))) {
        return true;
      }
      return false;
    });
  }

  handleFileSize() {
    if (this.file?.size / 1024 / 1024 > this.maxVideoSize) {
      this.toastrService.error(
        `The video size exceeds ${this.maxVideoSize} MB. Please select a different file.`,
      );
      this.file = null;
    } else if (this.isDropFile) {
      this.upload();
    }
  }
  removeFile() {
    this.file = null;
    this.progress = 0;
    this.whenRemoveFile.emit();
  }
  upload() {
    this.icon = null;
    this.mediaService.uploadMedia({ file: this.file }, true).subscribe({
      next: (event) => {
        if (event.type === HttpEventType.UploadProgress) {
          this.progress = Math.round((event.loaded / event.total) * 100);
        } else if (event.type === HttpEventType.Response) {
          this.completed.emit(event.body);
          this.icon = DONE_ICON;
          this.whenUpload.emit(event);
          this.isSuccess = true;
          if (!this.isDropFile) {
            this.file = null;
          }
        }
      },
      error: () => {
        this.icon = BLOCK_ICON;
      },
    });
  }
  clickFileInput() {
    this.fileInput.nativeElement.click();
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;

    if (event.dataTransfer?.files) {
      Array.from(event.dataTransfer?.files).forEach((file) => {
        this.fileInputOnChange(file, true);
      });
    }
  }
}
